import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import BottomSheet from './BottomSheet';
import { formatRelative } from '../../utils/formatting';
import FaCalendarIcon from './FaCalendarIcon';
import FaTrophyIcon from './FaTrophyIcon';
import PillToggleGroup from '../ui/PillToggleGroup';
import { lazyImport } from '../../utils/lazyImport';
import { loadPushNamespace } from '../../i18n/loadPushNamespace';

// Push settings (« Réglages » tab): own chunk with the `push` namespace,
// fetched the first time the tab is opened.
const NotificationSettingsContent = lazyImport(() => Promise.all([
  import('../notifications/NotificationSettingsSheet'),
  loadPushNamespace(),
]).then(([m]) => ({ default: m.NotificationSettingsContent })));

// Font Awesome Free v7.3.1 (solid bell / solid video), CC BY 4.0 —
// https://fontawesome.com/license/free. Matches the app's other FA solid icons.
const FaBellIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" {...props}>
    <path d="M224 0c-17.7 0-32 14.3-32 32l0 3.2C119 50 64 114.6 64 192l0 21.7c0 48.1-16.4 94.8-46.4 132.4L7.8 358.3C2.7 364.6 0 372.4 0 380.5 0 400.1 15.9 416 35.5 416l376.9 0c19.6 0 35.5-15.9 35.5-35.5 0-8.1-2.7-15.9-7.8-22.2l-9.8-12.2C400.4 308.5 384 261.8 384 213.7l0-21.7c0-77.4-55-142-128-156.8l0-3.2c0-17.7-14.3-32-32-32zM162 464c7.1 27.6 32.2 48 62 48s54.9-20.4 62-48l-124 0z" />
  </svg>
);
const FaVideoIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" {...props}>
    <path d="M96 64c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64L96 64zM464 336l73.5 58.8c4.2 3.4 9.4 5.2 14.8 5.2 13.1 0 23.7-10.6 23.7-23.7l0-240.6c0-13.1-10.6-23.7-23.7-23.7-5.4 0-10.6 1.8-14.8 5.2L464 176 464 336z" />
  </svg>
);

// Backend-emitted access_change discriminators — see backend/services/subscriptionService.js.
// Same list NotificationSidebar uses; duplicated rather than imported because
// NotificationSidebar must stay byte-identical (it is the coach-facing surface).
const ACCESS_CHANGE_EVENTS = ['downgrade', 'restore', 'restore_failed'];

/**
 * Icon per notification type — matches the Figma export (calendar, speech
 * bubble, video camera, trophy). `session_assigned` uses the calendar (a new
 * session on the planning), `league_change` the trophy (rankings).
 */
const ICON_BY_TYPE = {
  video_upload: FaVideoIcon,
  video_feedback: FaVideoIcon,
  video_validated: FaVideoIcon,
  access_change: FaBellIcon,
  session_assigned: FaCalendarIcon,
  league_change: FaTrophyIcon,
  match_plan_shared: FaTrophyIcon,
  questionnaire_due: FaCalendarIcon,
  // Coach-only types (the coach's phone opens this same sheet).
  set_corrected: FaVideoIcon,
  questionnaire_submitted: FaCalendarIcon,
};

/**
 * Split a notification into the bold segment and the plain segment the design
 * asks for.
 *
 * The bold half is NOT guessed from the composed sentence: the payload keeps
 * the actor (`studentName` / `coachName`) and the session/exercise in separate
 * `data` fields, so the split is structural. The backend also stores a
 * pre-composed FRENCH `title`/`message`; those are ignored for known shapes so
 * an English-mode student never sees French chrome — exactly the rule
 * NotificationSidebar already enforces.
 */
const resolveContent = (notif, t) => {
  if (
    notif.type === 'video_upload' ||
    notif.type === 'video_feedback' ||
    notif.type === 'video_validated'
  ) {
    const exerciseName = notif.data?.exerciseName || notif.exerciseName || t('fallback.exercise');
    const sessionName = notif.data?.sessionName || notif.sessionName || '';
    const setNumber = notif.data?.setNumber || notif.setInfo?.setNumber;
    // setIndex carries totalSets in the backend payload.
    const totalSets = notif.data?.setIndex || notif.data?.totalSets || notif.setInfo?.setIndex;

    let exerciseDisplay;
    if (setNumber && totalSets) {
      exerciseDisplay = `${exerciseName} ${setNumber}/${totalSets}`;
    } else if (setNumber) {
      exerciseDisplay = `${exerciseName} ${setNumber}/${setNumber}`;
    } else {
      exerciseDisplay = exerciseName;
    }

    const isFromCoach = notif.type !== 'video_upload';
    const actor = isFromCoach
      ? (notif.coachName || notif.data?.coachName || t('fallback.coach'))
      : (notif.studentName || notif.data?.studentName || t('fallback.client'));

    return {
      strong: actor,
      detail: sessionName ? `${sessionName} - ${exerciseDisplay}` : exerciseDisplay,
    };
  }

  if (notif.type === 'access_change') {
    const event = notif.data?.event;
    if (ACCESS_CHANGE_EVENTS.includes(event)) {
      return {
        strong: t(`notifications.access_change.${event}.title`),
        detail: t(`notifications.access_change.${event}.message`),
      };
    }
    // Legacy rows with an unknown/missing event: the stored strings are all there is.
    return { strong: notif.title || t('fallback.access'), detail: notif.message || '' };
  }

  if (notif.type === 'session_assigned') {
    const d = notif.data || {};
    const count = Number(d.count) || 1;
    return {
      strong: d.coachName || t('fallback.coach'),
      detail: count > 1
        ? t('notifications.session_assigned.many', { count })
        : t('notifications.session_assigned.one', { session: d.sessionName || t('notifications.session_assigned.default_session') }),
    };
  }

  if (notif.type === 'match_plan_shared') {
    const d = notif.data || {};
    return {
      strong: d.coachName || t('fallback.coach'),
      detail: [t('notifications.match_plan_shared'), d.competitionName].filter(Boolean).join(' · '),
    };
  }

  if (notif.type === 'questionnaire_due') {
    const d = notif.data || {};
    return {
      strong: t(`notifications.questionnaire_due.${d.kind === 'onboarding' ? 'onboarding' : 'weekly'}`),
      detail: d.title || '',
    };
  }

  // Coach-only types — same wording as NotificationSidebar, from `data`.
  if (notif.type === 'set_corrected') {
    const d = notif.data || {};
    const count = Number(d.correctedSets) || 1;
    const exercises = (d.exerciseNames || []).join(', ');
    const what = count > 1
      ? t('notifications.set_corrected.many', { count, exercises })
      : t('notifications.set_corrected.one', { exercises });
    return {
      strong: d.studentName || notif.studentName || t('fallback.client'),
      detail: d.sessionName ? `${d.sessionName} - ${what}` : what,
    };
  }

  if (notif.type === 'questionnaire_submitted') {
    const d = notif.data || {};
    return {
      strong: d.studentName || notif.studentName || t('fallback.client'),
      detail: t(`notifications.questionnaire_submitted.${d.kind === 'onboarding' ? 'onboarding' : 'weekly'}`),
    };
  }

  if (notif.type === 'candidature_received') {
    const d = notif.data || {};
    return {
      strong: d.prospectName || d.studentName || t('fallback.client'),
      detail: t('notifications.candidature_received'),
    };
  }

  if (notif.type === 'league_change') {
    const d = notif.data || {};
    const key = d.direction === 'relegation' ? 'relegation' : 'promotion';
    return {
      strong: t(`notifications.league_change.${key}.title`),
      detail: t(`notifications.league_change.${key}.message`, { league: d.toLeague || '' }),
    };
  }

  return {
    strong: notif.title || notif.exerciseName || t('fallback.exercise'),
    detail: notif.message || '',
  };
};

/**
 * Content for a grouped row (count > 1): actor + "Session - Exercice · N
 * vidéos", without per-set numbers — the sets are exactly what the group folds
 * away. Video groups are always coach-authored (video_upload never reaches
 * this sheet).
 */
const resolveGroupContent = (latest, count, t) => {
  const d = latest.data || {};
  const exerciseName = d.exerciseName || latest.exerciseName || t('fallback.exercise');
  const sessionName = d.sessionName || latest.sessionName || '';
  const base = sessionName ? `${sessionName} - ${exerciseName}` : exerciseName;
  const actor = latest.coachName || d.coachName || t('fallback.coach');
  return { strong: actor, detail: `${base} · ${t('notifications.video_count', { count })}` };
};

/**
 * Student notifications bottom sheet (Figma) — opened by the bell in
 * <DashboardHeader>.
 *
 * Purely presentational: `notifications` and every callback come from the ONE
 * fetch that <Header> owns, published through MainLayout's header-actions
 * registry. It does not fetch, subscribe or mutate anything itself.
 *
 * It does NOT replace <NotificationSidebar>: that component is mounted by
 * <Header>, which serves coaches too, and is a right-hand sidebar by design.
 * Turning it into a bottom sheet would change the coach experience, so this is
 * a second, student-only surface over the same data and the same
 * `notifications.*` i18n keys.
 */
const StudentNotificationsSheet = ({
  isOpen,
  onClose,
  notifications = [],
  onNotificationClick,
  onMarkAllAsRead,
  role = 'student',
}) => {
  const { t } = useTranslation('common');
  // 'list' (received) | 'settings' (push settings). Every opening starts on the list.
  const [view, setView] = useState('list');
  useEffect(() => {
    if (!isOpen) setView('list');
  }, [isOpen]);

  // `video_upload` rows are addressed to the COACH (user_id = coachId, see
  // backend/utils/videoUtils.js) — any that reach a student account only echo
  // the student's own uploads, so they are pure noise here and dropped.
  // A coach (phone, same sheet) keeps them: they are the coach's main feed.
  const visibleNotifications = useMemo(
    () => (role === 'student' ? notifications.filter((n) => n.type !== 'video_upload') : notifications),
    [notifications, role]
  );

  const unreadCount = useMemo(
    () => visibleNotifications.filter((n) => n.read === false).length,
    [visibleNotifications]
  );

  // Coach feedback arrives one notification per SET ("Abducteur 1/4", "2/4"…).
  // Group the video types per assignment + exercise so a fully-reviewed
  // exercise is ONE row, not four. The list arrives newest-first; a group sits
  // at its newest member's position and shows that member's timestamp.
  const rows = useMemo(() => {
    const byKey = new Map();
    const out = [];
    visibleNotifications.forEach((notif, index) => {
      const d = notif.data || {};
      const groupable =
        (notif.type === 'video_feedback' || notif.type === 'video_validated') &&
        (d.assignmentId || d.sessionName || d.exerciseName);
      const key = groupable
        ? `${notif.type}|${d.assignmentId || d.sessionName}|${d.exerciseName || ''}`
        : `single-${notif.id || index}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.notifs.push(notif);
      } else {
        const row = { key, notifs: [notif] };
        byKey.set(key, row);
        out.push(row);
      }
    });
    return out;
  }, [visibleNotifications]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="student-notifications-sheet-title"
      testId="notifications-sheet"
      backdropTestId="notifications-sheet-backdrop"
      expandable
    >
      {/* Header row: title + "mark all as read". The title takes the app's
          section-header treatment (accent, semibold, tracked — see RisCard /
          SessionOfDayCard) instead of a heavy white heading. */}
      <div className="flex items-center justify-between gap-3 px-5 pt-2 pb-4">
        <h2
          id="student-notifications-sheet-title"
          className="inline-flex items-center gap-[6px] text-[12px] font-semibold tracking-[0.72px] text-kaiylo-accent"
        >
          <FaBellIcon className="h-[14px] w-[14px]" aria-hidden="true" />
          {t('notifications.title')}
        </h2>
        {view === 'list' && onMarkAllAsRead && visibleNotifications.length > 0 && (
          <button
            type="button"
            onClick={onMarkAllAsRead}
            data-testid="notifications-sheet-mark-all"
            disabled={unreadCount === 0}
            className="flex min-h-[44px] shrink-0 items-center gap-1.5 text-[12px] font-medium text-kaiylo-accent transition-opacity disabled:opacity-40"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            <span>{t('notifications.mark_all_read')}</span>
          </button>
        )}
      </div>

      <div className="px-5 pb-4">
        <PillToggleGroup
          asTabs
          ariaLabel={t('notifications.title')}
          options={[
            { value: 'list', label: t('notifications.tab_received') },
            { value: 'settings', label: t('notifications.tab_settings') },
          ]}
          value={view}
          onSelect={setView}
          testIdPrefix="notifications-sheet-tab"
        />
      </div>

      {view === 'settings' ? (
        <div
          data-testid="notifications-sheet-settings"
          className="max-h-[60dvh] min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-2 [[data-sheet-resized]_&]:max-h-none"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <React.Suspense fallback={<div aria-busy="true" className="h-11 rounded-[14px] bg-white/5" />}>
            <NotificationSettingsContent role={role} variant="sheet" embedded />
          </React.Suspense>
        </div>
      ) : (
      <>
      {/* List. Capped height so a long list scrolls inside the sheet instead of
          pushing it past the viewport; once the student swipes the sheet
          taller, the cap goes and the list fills the sheet.
          -webkit-overflow-scrolling keeps iOS momentum scrolling inside the
          portal. overscroll-contain: a swipe down that brings the list back
          to its top must not carry on into the page (pull-to-refresh). */}
      <div
        className="max-h-[60dvh] min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-1 [[data-sheet-resized]_&]:max-h-none"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {visibleNotifications.length === 0 ? (
          <p
            data-testid="notifications-sheet-empty"
            className="py-10 text-center text-[13px] font-light text-[rgba(255,255,255,0.25)]"
          >
            {t('notifications.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((row) => {
              const notif = row.notifs[0];
              const count = row.notifs.length;
              const isUnread = row.notifs.some((n) => n.read === false);
              const { strong, detail } =
                count > 1 ? resolveGroupContent(notif, count, t) : resolveContent(notif, t);
              const Icon = ICON_BY_TYPE[notif.type] || FaBellIcon;

              return (
                <li key={row.key}>
                  {/* A real <button>, so the row is reachable and activatable by
                      keyboard. min-h 56px clears the 44px tap-target floor. */}
                  {/* Unread rows are told apart by a lighter background only
                      (bg 0.05, the card fill of RisCard) — no outline; read
                      rows fall back to the inner-row bg the session card uses
                      for its exercise lines. Both keep a transparent 1px
                      border so the rows stay the same height. */}
                  <button
                    type="button"
                    data-testid="notifications-sheet-row"
                    onClick={() => onNotificationClick && onNotificationClick(notif)}
                    className={`flex min-h-[56px] w-full items-center gap-3 rounded-[10px] px-3 py-3 text-left transition-colors ${
                      isUnread
                        ? 'border border-transparent bg-[rgba(255,255,255,0.05)]'
                        : 'border border-transparent bg-[rgba(0,0,0,0.3)]'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(0,0,0,0.5)] text-kaiylo-accent"
                    >
                      <Icon className="h-4 w-4" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] leading-tight">
                        <span className="font-semibold text-white">{strong}</span>
                        {detail ? (
                          <span className="font-normal text-[rgba(255,255,255,0.75)]"> {detail}</span>
                        ) : null}
                      </span>
                      <span className="mt-1 block text-[11px] font-light text-[rgba(255,255,255,0.5)]">
                        {formatRelative(notif.timestamp || notif.createdAt)}
                      </span>
                      {/* Unread is conveyed by the copper dot AND by this
                          visually-hidden text, so it is never colour-only. */}
                      {isUnread && (
                        <span className="sr-only">{t('notifications.unread_aria')}</span>
                      )}
                    </span>

                    {isUnread && (
                      <span
                        aria-hidden="true"
                        data-testid="notifications-sheet-unread-dot"
                        className="h-2 w-2 shrink-0 rounded-full bg-kaiylo-accent"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </>
      )}
    </BottomSheet>
  );
};

export default StudentNotificationsSheet;
